import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  VStack,
  FormControl,
  FormLabel,
  FormErrorMessage,
  FormHelperText,
  Input,
  Button,
  HStack,
  Box,
  Text,
  Heading,
  useColorModeValue,
  IconButton,
  InputGroup,
  InputRightElement,
  Spinner,
  useToast,
  Badge,
  Flex,
  Grid,
  GridItem,
  Alert,
  AlertIcon,
  ButtonGroup,
  Card,
  useBreakpointValue,
  Tooltip,
  Fade,
  MenuButton,
  Menu,
  MenuList,
  MenuItem,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton
} from '@chakra-ui/react';
import {
  MagnifyingGlass,
  Globe,
  MapPin,
  Crosshair,
  PencilSimple,
  DotsThreeVertical,
  CopySimple,
  MapTrifold,
  CaretDown,
  Check, // Added Check icon
  Plus, // For Zoom In
  Minus, // For Zoom Out
  CornersOut, // For Enter Fullscreen
  CornersIn, // For Exit Fullscreen
  NumberCircleOne // For Manual coordinates
} from '@phosphor-icons/react';
import { useCreateSite } from '../../../context/CreateSiteContext';
import { motion, AnimatePresence } from 'framer-motion';
import { useJsApiLoader, GoogleMap, Marker, Autocomplete } from '@react-google-maps/api';

const MotionBox = motion(Box);

// Custom hook to extract all theme values
const useThemeColors = () => {
  // All Chakra UI hooks must be called here, unconditionally
  return {
    bgColor: useColorModeValue('white', 'bg.surface'),
    accentColor: useColorModeValue('yellow.50', 'rgba(214, 173, 96, 0.1)'),
    borderColor: useColorModeValue('yellow.200', 'rgba(214, 173, 96, 0.3)'),
    textColor: useColorModeValue('text.default', 'text.default'),
    mutedColor: useColorModeValue('text.muted', 'text.muted'),
    cardBg: useColorModeValue('bg.surface', 'bg.surface'),
    inputBg: useColorModeValue('gray.50', 'bg.accent'),
    dividerColor: useColorModeValue('gray.200', 'border.default')
  };
};

// Map styling to match "Explorer's Journal" theme
const defaultCenter = {
  lat: 51.178882, // Default to Stonehenge location
  lng: -1.826215
};

// Libraries needed for Google Maps
const libraries = ['places'];

// MapControlsOverlay component for map controls
const MapControlsOverlay = ({ 
  onZoomIn, 
  onZoomOut, 
  onMyLocation, 
  onToggleFullScreen,
  isFullScreen 
}) => {
  return (
    <Box
      position="absolute"
      top={3}
      right={3}
      zIndex={10}
      bg="white"
      borderRadius="md"
      boxShadow="md"
      p={1}
    >
      <VStack spacing={1}>
        <Tooltip label="Zoom in" placement="left">
          <IconButton
            icon={<Plus size={16} />}
            onClick={onZoomIn}
            aria-label="Zoom in"
            size="sm"
            variant="ghost"
          />
        </Tooltip>
        
        <Tooltip label="Zoom out" placement="left">
          <IconButton
            icon={<Minus size={16} />}
            onClick={onZoomOut}
            aria-label="Zoom out"
            size="sm"
            variant="ghost"
          />
        </Tooltip>
        
        <Tooltip label="My location" placement="left">
          <IconButton
            icon={<Crosshair size={16} />}
            onClick={onMyLocation}
            aria-label="My location"
            size="sm"
            variant="ghost"
          />
        </Tooltip>
        
        <Tooltip label={isFullScreen ? "Exit full screen" : "Full screen"} placement="left">
          <IconButton
            icon={isFullScreen ? <CornersIn size={16} /> : <CornersOut size={16} />}
            onClick={onToggleFullScreen}
            aria-label={isFullScreen ? "Exit full screen" : "Full screen"}
            size="sm"
            variant="ghost"
          />
        </Tooltip>
      </VStack>
    </Box>
  );
};

export const LocationStep = () => {
  // First, call all hooks unconditionally at the top level
  const {
    siteData,
    setSiteData,
    validationErrors,
  } = useCreateSite();

  // Google Maps API loading
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '',
    libraries
  });

  // Local state hooks
  const [map, setMap] = useState(null);
  const [autocompleteValue, setAutocompleteValue] = useState(siteData.name || '');
  const [mapZoom, setMapZoom] = useState(12);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [isAutocompleteWidgetReady, setIsAutocompleteWidgetReady] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false); // Track input focus state
  const [isSearching, setIsSearching] = useState(false); // Track search state
  
  // Manual coordinate entry state
  const [showManualEntryModal, setShowManualEntryModal] = useState(false);
  const [manualLat, setManualLat] = useState('');
  const [manualLng, setManualLng] = useState('');
  const [manualEntryError, setManualEntryError] = useState('');
  
  // Marker position state for consistent re-rendering
  const [markerPosition, setMarkerPosition] = useState(() =>
    siteData.location?.coordinates?.lat && siteData.location?.coordinates?.lng
      ? {
          lat: siteData.location.coordinates.lat,
          lng: siteData.location.coordinates.lng
        }
      : defaultCenter
  );
  
  // Responsive values
  const isMobile = useBreakpointValue({ base: true, md: false });
  const mapHeight = isFullScreen ? '80vh' : isMobile ? '300px' : '400px';
  
  // References
  const autocompleteRef = useRef(null); // For the Google Autocomplete service instance
  const htmlInputRef = useRef(null);    // For the actual HTML Input element
  const mapContainerRef = useRef(null);
  const toast = useToast();
  
  // Map container style
  const mapContainerStyle = useMemo(() => ({
    width: '100%',
    height: mapHeight,
    borderRadius: '8px',
    transition: 'height 0.3s ease'
  }), [mapHeight]);
  
  // Use our custom theme hook - all useColorModeValue calls are contained here
  const colors = useThemeColors();
  
  // Destructure for easier access
  const { bgColor, accentColor, borderColor, textColor, mutedColor, cardBg, inputBg, dividerColor } = colors;
  
  // Streamlined geocoding helper function
  const parseLocationResult = useCallback((place) => {
    const location = place.geometry.location;
    const lat = location.lat();
    const lng = location.lng();
    
    let addressData = {
      address: place.formatted_address || '',
      street: '',
      city: '',
      region: '',
      country: '',
      postcode: '',
      coordinates: { lat: Number(lat), lng: Number(lng) }
    };
    
    // Extract structured address components
    if (place.address_components) {
      for (const component of place.address_components) {
        const types = component.types;
        
        if (types.includes('street_number') || types.includes('route')) {
          addressData.street = addressData.street ? `${addressData.street} ${component.long_name}` : component.long_name;
        }
        if (types.includes('locality')) {
          addressData.city = component.long_name;
        }
        if (types.includes('administrative_area_level_1')) {
          addressData.region = component.long_name;
        }
        if (types.includes('country')) {
          addressData.country = component.long_name;
        }
        if (types.includes('postal_code')) {
          addressData.postcode = component.long_name;
        }
      }
    }
    
    return addressData;
  }, []);
  
  // Improved Find Site button handler
  const handleFindSiteClick = useCallback(async () => {
    if (!autocompleteValue || autocompleteValue.trim() === '') {
      toast({
        title: "Empty Search",
        description: "Please enter a site name or location to search.",
        status: "info",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    if (!isLoaded) {
      toast({
        title: "Map not loaded",
        description: "Please wait for the map to load.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    setIsSearching(true);
    
    try {
      const geocoder = new window.google.maps.Geocoder();
      
      // First try with enhanced search terms for megalithic sites
      const enhancedSearchTerm = `${autocompleteValue} megalithic site`;
      
      toast({
        title: "Searching...",
        description: "Looking for your site in Google Maps...",
        status: "info",
        duration: 2000,
        isClosable: true,
      });
      
      // Try enhanced search first
      geocoder.geocode({ address: enhancedSearchTerm }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const addressData = parseLocationResult(results[0]);
          setSiteData({ ...siteData, location: addressData });
          
          // Force marker re-render with new position by briefly clearing it
          setMarkerPosition(null);
          setTimeout(() => {
            setMarkerPosition({ lat: addressData.coordinates.lat, lng: addressData.coordinates.lng });
          }, 50);
          
          if (map) {
            map.panTo({ lat: addressData.coordinates.lat, lng: addressData.coordinates.lng });
            setMapZoom(15);
          }
          
          toast({
            title: "Location Found",
            description: `Located: ${addressData.address}`,
            status: "success",
            duration: 3000,
            isClosable: true,
          });
          setIsSearching(false);
        } else {
          // Fallback to original search term
          geocoder.geocode({ address: autocompleteValue }, (results, status) => {
            if (status === 'OK' && results[0]) {
              const addressData = parseLocationResult(results[0]);
              setSiteData({ ...siteData, location: addressData });
              
              // Force marker re-render with new position by briefly clearing it
              setMarkerPosition(null);
              setTimeout(() => {
                setMarkerPosition({ lat: addressData.coordinates.lat, lng: addressData.coordinates.lng });
              }, 50);
              
              if (map) {
                map.panTo({ lat: addressData.coordinates.lat, lng: addressData.coordinates.lng });
                setMapZoom(15);
              }
              
              toast({
                title: "Location Found",
                description: `Located: ${addressData.address}`,
                status: "success",
                duration: 3000,
                isClosable: true,
              });
            } else {
              toast({
                title: "Location Not Found",
                description: "Try entering coordinates manually or clicking on the map.",
                status: "warning",
                duration: 3000,
                isClosable: true,
              });
            }
            setIsSearching(false);
          });
        }
      });
    } catch (error) {
      console.error('Geocoding error:', error);
      toast({
        title: "Search Error",
        description: "An error occurred while searching. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      setIsSearching(false);
    }
  }, [autocompleteValue, isLoaded, toast, parseLocationResult, setSiteData, siteData, map, setMarkerPosition, setMapZoom]);
  
  // Sync markerPosition when siteData changes
  useEffect(() => {
    if (siteData.location?.coordinates?.lat && siteData.location?.coordinates?.lng) {
      setMarkerPosition({
        lat: siteData.location.coordinates.lat,
        lng: siteData.location.coordinates.lng
      });
    }
  }, [siteData.location?.coordinates?.lat, siteData.location?.coordinates?.lng]);
  
  // Handle map load
  const onMapLoad = useCallback((map) => {
    setMap(map);
  }, []);

  // Handle map unload
  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);
  
  // Handle map click for location selection
  const onMapClick = useCallback((event) => {
    // Get clicked coordinates
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
    
    // Ensure coordinates are valid numbers
    const parsedLat = parseFloat(lat);
    const parsedLng = parseFloat(lng);
    
    if (isNaN(parsedLat) || isNaN(parsedLng)) {
      toast({
        title: "Error",
        description: "Could not determine coordinates from your selection",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    // Use Google Maps Geocoder API to get address information for clicked location
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const place = results[0];
        
        // Parse address components similar to onPlaceSelected
        let addressData = {
          address: place.formatted_address || '',
          street: '',
          city: '',
          region: '',
          country: '',
          postcode: '',
          coordinates: {
            lat: Number(parsedLat),
            lng: Number(parsedLng)
          }
        };
        
        // Extract structured address components
        if (place.address_components) {
          for (const component of place.address_components) {
            const types = component.types;
            
            if (types.includes('street_number') || types.includes('route')) {
              addressData.street = addressData.street
                ? `${addressData.street} ${component.long_name}`
                : component.long_name;
            }
            if (types.includes('locality')) {
              addressData.city = component.long_name;
            }
            if (types.includes('administrative_area_level_1')) {
              addressData.region = component.long_name;
            }
            if (types.includes('country')) {
              addressData.country = component.long_name;
            }
            if (types.includes('postal_code')) {
              addressData.postcode = component.long_name;
            }
          }
        }
        
        // Update site data with location information - ensure coordinates are proper numbers
        setSiteData({
          ...siteData,
          location: {
            ...addressData,
            coordinates: {
              lat: Number(parsedLat),
              lng: Number(parsedLng)
            }
          }
        });
        
        // Keep site name in the autocomplete field
        setAutocompleteValue(siteData.name || '');
        
        // Center map on selected location
        if (map) {
          map.panTo({ lat, lng });
        }
        
        // Show success toast
        toast({
          title: "Location updated",
          description: `Selected: ${addressData.address}`,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
    });
  }, [setSiteData, siteData, map, toast]);

  // Handle place selection from autocomplete
  const onPlaceSelected = useCallback(() => {
    let place = null;
    if (autocompleteRef.current) {
      place = autocompleteRef.current.getPlace();
      
      // Add debugging log
      console.log("Place selected:", place);
      
      // Check if place has proper data
      if (!place || !place.geometry) {
        console.warn("Invalid place selected or missing geometry", place);
        toast({
          title: "Selection issue",
          description: "Please use the Find Site button for this location instead",
          status: "warning",
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      
      if (place.geometry && place.geometry.location) {
        // Get coordinates
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        
        // Ensure coordinates are valid numbers
        const parsedLat = parseFloat(lat);
        const parsedLng = parseFloat(lng);
        
        if (isNaN(parsedLat) || isNaN(parsedLng)) {
          toast({
            title: "Error",
            description: "Could not determine coordinates from your selection",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
          return;
        }
        
        // Parse address components
        let addressData = {
          address: place.formatted_address || '',
          street: '',
          city: '',
          region: '',
          country: '',
          postcode: '',
          coordinates: {
            lat: Number(parsedLat),
            lng: Number(parsedLng)
          }
        };
        
        // Extract structured address components
        if (place.address_components) {
          for (const component of place.address_components) {
            const types = component.types;
            
            if (types.includes('street_number') || types.includes('route')) {
              addressData.street = addressData.street
                ? `${addressData.street} ${component.long_name}`
                : component.long_name;
            }
            if (types.includes('locality')) {
              addressData.city = component.long_name;
            }
            if (types.includes('administrative_area_level_1')) {
              addressData.region = component.long_name;
            }
            if (types.includes('country')) {
              addressData.country = component.long_name;
            }
            if (types.includes('postal_code')) {
              addressData.postcode = component.long_name;
            }
          }
        }
        
        // Update site data with location information - ensure coordinates are proper numbers
        setSiteData({
          ...siteData,
          location: {
            ...addressData,
            coordinates: {
              lat: Number(parsedLat),
              lng: Number(parsedLng)
            }
          }
        });
        
        // Update marker position explicitly with Number for consistent types
        setMarkerPosition({
          lat: Number(parsedLat),
          lng: Number(parsedLng)
        });
        
        // Force marker to update by using a timeout
        setTimeout(() => {
          if (map) {
            // Force re-render by slightly moving and then going back
            map.panBy(1, 1);
            setTimeout(() => {
              map.panBy(-1, -1);
            }, 10);
          }
        }, 100);
        
        // Keep site name in the autocomplete field
        setAutocompleteValue(siteData.name || '');
        
        // Show success toast
        toast({
          title: "Location updated",
          description: `Selected: ${addressData.address}`,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        
        // Center map on the selected location with animation
        if (map) {
          map.panTo({ lat: Number(parsedLat), lng: Number(parsedLng) });
          setMapZoom(15);
          
          // Add a slight bounce effect to draw attention to the marker
          setTimeout(() => {
            setMarkerPosition({
              lat: Number(parsedLat),
              lng: Number(parsedLng)
            });
          }, 150);
        }
      }
    }
  }, [setSiteData, siteData, toast, map]);

  // Handle direct input in the autocomplete field
  const handleAutocompleteChange = useCallback((e) => {
    // Only update the autocomplete visible value, don't modify the site location address
    setAutocompleteValue(e.target.value);
    
    // Don't update the address in the site data to preserve location information
    // but store the search query for later use
    setSiteData(prevData => ({
      ...prevData,
      searchQuery: e.target.value // Store search query separately
    }));
  }, [setSiteData]);

  // Enhanced function to trigger the Autocomplete dropdown
  const triggerAutocompleteDropdown = useCallback(() => {
    const gmapsServiceInstance = autocompleteRef.current; // Google Maps Autocomplete service
    const inputElement = htmlInputRef.current; // Actual HTML input element

    if (!isLoaded) {
      console.warn("triggerAutocompleteDropdown: Google Maps API not loaded.");
      toast({
        title: "Map service not ready",
        description: "Please wait a moment for Google Maps to initialize.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!inputElement) {
      console.warn("triggerAutocompleteDropdown: HTML input element not available.");
      // This case should ideally not happen if the component is rendered correctly.
      return;
    }

    if (!gmapsServiceInstance) {
      console.warn("triggerAutocompleteDropdown: Google Autocomplete service not ready.");
      toast({
        title: "Search service initializing",
        description: "The location search is still loading. Please try again in a moment.",
        status: "info",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!autocompleteValue || autocompleteValue.trim() === '') {
      console.warn("triggerAutocompleteDropdown: No autocomplete value to search for.");
      // Optionally, inform the user if they click search with no text,
      // though handleSearchClick already does this.
      // toast({
      //   title: "Enter search text",
      //   description: "Type a location name or address to search.",
      //   status: "info",
      //   duration: 3000
      // });
      return;
    }

    // Ensure the input field is focused
    if (document.activeElement !== inputElement) {
      inputElement.focus();
    }

    // Ensure the input's value is up-to-date with the state
    // This is important if the value was set programmatically (e.g. from siteData.name)
    if (inputElement.value !== autocompleteValue) {
      inputElement.value = autocompleteValue;
    }

    // Append search terms to improve results for megalithic sites
    const enhanceSearchTerms = () => {
      // Only enhance if it doesn't already contain these terms
      const lowerValue = autocompleteValue.toLowerCase();
      if (!lowerValue.includes('megalith') &&
          !lowerValue.includes('stone') &&
          !lowerValue.includes('ancient') &&
          !lowerValue.includes('monument') &&
          !lowerValue.includes('archaeo')) {
        
        // Check if the search appears to be for a megalithic site based on keywords
        if (lowerValue.includes('circle') ||
            lowerValue.includes('menhir') ||
            lowerValue.includes('dolmen') ||
            lowerValue.includes('barrow') ||
            lowerValue.includes('cairn') ||
            lowerValue.includes('standing') ||
            lowerValue.includes('henge') ||
            lowerValue.includes('tomb') ||
            lowerValue.includes('tumulus') ||
            lowerValue.includes('cromlech')) {
          // Add "megalithic" to help find it
          return `${autocompleteValue} megalithic site`;
        }
      }
      return autocompleteValue;
    };
    
    // Try with enhanced search terms if appropriate
    const searchValue = enhanceSearchTerms();
    if (searchValue !== autocompleteValue) {
      inputElement.value = searchValue;
    }

    // Dispatch an 'input' event to trigger the Autocomplete suggestions
    setTimeout(() => {
      if (inputElement && window.google && window.google.maps && window.google.maps.event) {
        const inputEvent = new Event('input', { bubbles: true, cancelable: true });
        inputElement.dispatchEvent(inputEvent);

        // Now enable the ArrowDown simulation to ensure dropdown appears
        setTimeout(() => {
          if (document.activeElement === inputElement) {
            const arrowDownEvent = new KeyboardEvent('keydown', {
              key: 'ArrowDown',
              code: 'ArrowDown',
              keyCode: 40,
              which: 40,
              bubbles: true,
              cancelable: true,
            });
            inputElement.dispatchEvent(arrowDownEvent);
          }
        }, 100);
      }
    }, 50); // A small delay (e.g., 50ms)

  }, [isLoaded, autocompleteValue, autocompleteRef, htmlInputRef, toast]);

  const handleInputFocus = useCallback(() => {
    setIsInputFocused(true);
    // If there's already a value (e.g., site name pre-filled), try to open dropdown
    if (autocompleteValue) {
      triggerAutocompleteDropdown();
    }
  }, [autocompleteValue, triggerAutocompleteDropdown]);

  const handleInputBlur = useCallback(() => {
    setIsInputFocused(false);
  }, []);

  const handleSearchClick = useCallback(() => {
    // Now just calls the shared trigger function
    triggerAutocompleteDropdown();
    // Optionally, provide feedback if it couldn't trigger
    const gmapsServiceInstance = autocompleteRef.current;
    const inputElement = htmlInputRef.current;
    if (!(isLoaded && inputElement && gmapsServiceInstance && autocompleteValue)) {
      if (!isLoaded) {
        toast({ title: "Map not loaded", description: "Please wait for the map to load.", status: "warning", duration: 3000, isClosable: true });
      } else if (!autocompleteValue) {
        toast({ title: "Empty search", description: "Please enter a location to search.", status: "info", duration: 3000, isClosable: true });
      } else if (!inputElement || !gmapsServiceInstance) {
        toast({ title: "Autocomplete not ready", description: "The search component is not yet available.", status: "warning", duration: 3000, isClosable: true });
      }
    }
  }, [triggerAutocompleteDropdown, isLoaded, autocompleteValue, toast, autocompleteRef, htmlInputRef]);

  // Always ensure the site name is displayed in the autocomplete field
  useEffect(() => {
    // Always prioritize showing the site name in the location input field
    if (siteData.name) {
      setAutocompleteValue(siteData.name);
    }
  }, [siteData.name]);

  useEffect(() => {
    if (!isLoaded) {
      setIsAutocompleteWidgetReady(false);
      // autocompleteRef.current = null; // The ref will be reset if Autocomplete unmounts/reloads
    }
  }, [isLoaded]);
 
  // Force coordinates to be numbers for validation
  useEffect(() => {
    if (siteData.location?.coordinates) {
      const lat = siteData.location.coordinates.lat;
      const lng = siteData.location.coordinates.lng;
      
      // Check if coordinates exist but aren't proper numbers
      if ((lat !== undefined && lng !== undefined) && 
          (typeof lat !== 'number' || typeof lng !== 'number' || isNaN(lat) || isNaN(lng))) {
        
        // Fix coordinates by ensuring they are proper numbers
        const fixedLat = Number(parseFloat(lat));
        const fixedLng = Number(parseFloat(lng));
        
        if (!isNaN(fixedLat) && !isNaN(fixedLng)) {
          // Update the site data with proper number coordinates
          setSiteData({
            ...siteData,
            location: {
              ...siteData.location,
              coordinates: {
                lat: fixedLat,
                lng: fixedLng
              }
            }
          });
        }
      }
    }
  }, [siteData.location?.coordinates, setSiteData]);
  
  // Copy coordinates to clipboard
  const copyCoordinates = useCallback(() => {
    if (!siteData.location?.coordinates) return;
    
    const { lat, lng } = siteData.location.coordinates;
    const coordsText = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
    
    navigator.clipboard.writeText(coordsText).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
      
      toast({
        title: "Copied to clipboard",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    });
  }, [siteData.location?.coordinates, toast]);
  
  // Map control handlers
  const handleZoomIn = useCallback(() => {
    if (map) {
      const currentZoom = map.getZoom();
      map.setZoom(currentZoom + 1);
      setMapZoom(currentZoom + 1);
    }
  }, [map]);
  
  const handleZoomOut = useCallback(() => {
    if (map) {
      const currentZoom = map.getZoom();
      map.setZoom(currentZoom - 1);
      setMapZoom(currentZoom - 1);
    }
  }, [map]);
  
  const handleMyLocation = useCallback(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          
          if (map) {
            map.panTo({ lat: latitude, lng: longitude });
            setMapZoom(15);
          }
          
          toast({
            title: "Location found",
            description: "Map centered on your current location",
            status: "info",
            duration: 2000,
            isClosable: true,
          });
        },
        (error) => {
          toast({
            title: "Location error",
            description: "Unable to get your current location",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
        }
      );
    } else {
      toast({
        title: "Not supported",
        description: "Geolocation is not supported by this browser",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  }, [map, toast]);
  
  const toggleFullScreen = useCallback(() => {
    setIsFullScreen(prev => !prev);
  }, []);
  
  // Handler for opening the manual coordinate entry modal
  const openManualCoordinatesModal = useCallback(() => {
    // If we already have coordinates, pre-fill the form with them for adjustment
    if (siteData.location?.coordinates?.lat && siteData.location?.coordinates?.lng) {
      setManualLat(siteData.location.coordinates.lat.toString());
      setManualLng(siteData.location.coordinates.lng.toString());
    }
    // Otherwise, if the map is centered somewhere, use those coordinates
    else if (map) {
      const center = map.getCenter();
      if (center) {
        setManualLat(center.lat().toFixed(6));
        setManualLng(center.lng().toFixed(6));
      }
    }
    // Open the modal
    setShowManualEntryModal(true);
  }, [siteData.location?.coordinates, map]);

  // Create memoized map component to avoid performance issues
  const mapComponent = useMemo(() => {
    // If location coordinates exist in site data, use them, otherwise use default
    const center = siteData.location?.coordinates?.lat && siteData.location?.coordinates?.lng
      ? { lat: siteData.location.coordinates.lat, lng: siteData.location.coordinates.lng }
      : defaultCenter;
    
    if (!isLoaded) {
      return (
        <Box
          height={mapHeight}
          display="flex"
          alignItems="center"
          justifyContent="center"
          bg={inputBg}
          borderRadius="md"
          transition="height 0.3s ease"
        >
          <Spinner size="lg" color="yellow.500" />
          <Text ml={4}>Loading map...</Text>
        </Box>
      );
    }
    
    return (
      <Box 
        borderWidth="1px" 
        borderRadius="md" 
        overflow="hidden" 
        position="relative"
        ref={mapContainerRef}
      >
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={center}
          zoom={mapZoom}
          onLoad={onMapLoad}
          onUnmount={onUnmount}
          onClick={onMapClick}
          options={{
            streetViewControl: false,
            mapTypeControlOptions: {
              mapTypeIds: ["roadmap", "satellite", "hybrid", "terrain"],
              style: 2,
            },
            mapTypeId: "hybrid",
            styles: [
              {
                featureType: "poi",
                elementType: "labels",
                stylers: [{ visibility: "off" }],
              },
            ],
            fullscreenControl: false,
            zoomControl: false // We'll use our own controls
          }}
        >
          {/* Conditionally render marker if coordinates are valid */}
          {siteData.location?.coordinates?.lat && siteData.location?.coordinates?.lng && (
            <Marker
              key={`${siteData.location.coordinates.lat}-${siteData.location.coordinates.lng}`}
              animation={window.google.maps.Animation.DROP}
              position={markerPosition}
              icon={{
                url: "/marker-main.svg", // Use the specified icon
                scaledSize: new window.google.maps.Size(40, 40),
                anchor: new window.google.maps.Point(20, 40)
              }}
            />
          )}
          
          {/* Custom map controls */}
          <MapControlsOverlay 
            onZoomIn={handleZoomIn}
            onZoomOut={handleZoomOut}
            onMyLocation={handleMyLocation}
            onToggleFullScreen={toggleFullScreen}
            isFullScreen={isFullScreen}
          />
        </GoogleMap>
        
        {/* Map instructions */}
        <Box
          position="absolute"
          bottom={3}
          left={3}
          bg="blackAlpha.700"
          color="white"
          px={3}
          py={1}
          borderRadius="md"
          fontSize="xs"
          maxW="60%"
        >
          <Text>Click on the map to select exact location</Text>
        </Box>
      </Box>
    );
  }, [
    isLoaded, siteData.location?.coordinates?.lat, siteData.location?.coordinates?.lng, 
    mapHeight, inputBg, mapContainerStyle, mapZoom, onMapLoad, onUnmount, onMapClick, 
    markerPosition, handleZoomIn, handleZoomOut, handleMyLocation, toggleFullScreen, isFullScreen
  ]);

  return (
    <Box>
      <VStack spacing={8} align="stretch">
        <Box>
          <Heading as="h1" size="lg" mb={2} fontFamily="heading">
            Site Location
          </Heading>
          <Text color={mutedColor}>
            Mark the exact location of the megalithic site
          </Text>
        </Box>
        
        <Grid templateColumns="repeat(12, 1fr)" gap={{ base: 4, md: 6 }}>
          {/* Location */}
          <GridItem colSpan={12}>
            <FormControl isRequired isInvalid={validationErrors['location.coordinates']}>
              <FormLabel htmlFor="location" fontWeight="medium">
                Location
              </FormLabel>
              <Box position="relative">
                {isLoaded ? (
                  <Box position="relative">
                    {/* Custom search input with absolute positioned icon */}
                    <Autocomplete
                      onLoad={serviceInstance => {
                        autocompleteRef.current = serviceInstance;
                        setIsAutocompleteWidgetReady(true);
                      }}
                      onPlaceChanged={() => {
                        console.log("Place changed event fired");
                        onPlaceSelected();
                      }}
                      options={{
                        componentRestrictions: { country: [] },
                        fields: ["address_components", "geometry", "name", "formatted_address", "place_id"],
                        types: ["geocode", "establishment"] // Revert to original working types
                      }}
                    >
                      <InputGroup>
                        <Input
                          ref={htmlInputRef}
                          id="location"
                          name="location"
                          placeholder="Search for site location or address"
                          value={autocompleteValue}
                          onChange={handleAutocompleteChange}
                          onFocus={handleInputFocus}
                          onBlur={handleInputBlur}
                          variant="filled"
                          bg={inputBg}
                          paddingRight="150px" // Increased to accommodate button
                        />
                        <InputRightElement width="auto" pr="8px">
                          <HStack spacing={2}>
                            <Tooltip label="Enter precise coordinates manually" placement="top">
                              <Box
                                as="button"
                                onClick={openManualCoordinatesModal}
                                p={2}
                                borderRadius="md"
                                bg="transparent"
                                color="gray.500"
                                _hover={{ 
                                  bg: "yellow.50", 
                                  color: "yellow.600",
                                  transform: "scale(1.1)" 
                                }}
                                _active={{ 
                                  bg: "yellow.100",
                                  transform: "scale(0.95)" 
                                }}
                                transition="all 0.2s"
                                cursor="pointer"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                              >
                                <NumberCircleOne size={16} />
                              </Box>
                            </Tooltip>
                            <Tooltip 
                              label="Search for what you typed - finds obscure sites that don't appear in dropdown" 
                              placement="top"
                              hasArrow
                              bg={useColorModeValue('gray.800', 'white')}
                              color={useColorModeValue('white', 'gray.800')}
                              fontSize="sm"
                              px={3}
                              py={2}
                              borderRadius="md"
                              maxW="250px"
                              textAlign="center"
                            >
                              <Box
                                as="button"
                                onClick={handleFindSiteClick}
                                p={2}
                                borderRadius="md"
                                bg="transparent"
                                color="gray.500"
                                _hover={{ 
                                  bg: "blue.50", 
                                  color: "blue.600",
                                  transform: "scale(1.1)" 
                                }}
                                _active={{ 
                                  bg: "blue.100",
                                  transform: "scale(0.95)" 
                                }}
                                transition="all 0.2s"
                                cursor="pointer"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                opacity={isSearching ? 0.7 : 1}
                                pointerEvents={isSearching ? "none" : "auto"}
                              >
                                {isSearching ? <Spinner size="sm" /> : <MagnifyingGlass size={16} />}
                              </Box>
                            </Tooltip>
                          </HStack>
                        </InputRightElement>
                      </InputGroup>
                    </Autocomplete>
                  </Box>
                ) : (
                  <Box position="relative">
                    <Input
                      placeholder="Loading Google Maps..."
                      variant="filled"
                      bg={inputBg}
                      isDisabled
                    />
                    <Box
                      position="absolute"
                      right="12px"
                      top="50%"
                      transform="translateY(-50%)"
                      zIndex={2}
                    >
                      <Spinner size="sm" color="yellow.500" />
                    </Box>
                  </Box>
                )}
              </Box>
              <FormHelperText>
                <b>For obscure sites:</b> The dropdown may not show results. Use the search icon (🔍) to auto-find what you typed. For precise coordinates, use the numbered icon (①).
              </FormHelperText>
              {validationErrors['location.coordinates'] && (
                <FormErrorMessage>{validationErrors['location.coordinates']}</FormErrorMessage>
              )}
            </FormControl>

            {/* Map controls for mobile */}
            {isMobile && (
              <HStack mt={3} mb={2} spacing={2}>
                <Button
                  leftIcon={<Globe />}
                  size="sm"
                  variant="outline"
                  onClick={handleMyLocation}
                  flexGrow={1}
                >
                  My Location
                </Button>
                
                <ButtonGroup size="sm" isAttached variant="outline">
                  <IconButton
                    icon={<Minus />}
                    aria-label="Zoom out"
                    onClick={handleZoomOut}
                  />
                  <IconButton
                    icon={<Plus />}
                    aria-label="Zoom in"
                    onClick={handleZoomIn}
                  />
                </ButtonGroup>
                
                <IconButton
                  icon={isFullScreen ? <CornersIn /> : <CornersOut />}
                  aria-label={isFullScreen ? "Exit full screen" : "Full screen"}
                  variant="outline"
                  onClick={toggleFullScreen}
                />
              </HStack>
            )}

            {/* Map display */}
            <Box mt={isMobile ? 2 : 4} mb={4}>
              {mapComponent}
            </Box>

            {/* Detailed location display */}
            {siteData.location && 
             siteData.location.coordinates && 
             typeof siteData.location.coordinates.lat === 'number' &&
             typeof siteData.location.coordinates.lng === 'number' && (
              <MotionBox
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                mt={3}
                p={4}
                borderRadius="md"
                bg={accentColor}
                borderWidth="1px"
                borderColor={borderColor}
              >
                <Grid templateColumns={{ base: '1fr', md: '1fr auto' }} gap={3}>
                  <GridItem>
                    <Text fontWeight="medium" mb={1}>Selected Location:</Text>
                    <Text>{siteData.location.address}</Text>
                    
                    <VStack spacing={2} align="start" mt={2}>
                      <HStack fontSize="sm" color={mutedColor}>
                        <MapPin size={16} />
                        <Text fontFamily="mono" fontWeight="medium">
                          {siteData.location.coordinates.lat.toFixed(8)}, {siteData.location.coordinates.lng.toFixed(8)}
                        </Text>
                        <Tooltip label="Copy coordinates" placement="top">
                          <IconButton
                            icon={copySuccess ? <Check size={14} /> : <CopySimple size={14} />}
                            size="xs"
                            aria-label="Copy coordinates"
                            onClick={copyCoordinates}
                            variant="ghost"
                            colorScheme={copySuccess ? "green" : "gray"}
                          />
                        </Tooltip>
                      </HStack>
                      
                      <Text fontSize="xs" color="gray.500">
                        Precision: ±{((1 / Math.pow(10, 8)) * 111000).toFixed(2)}m
                      </Text>
                    </VStack>
                  </GridItem>
                  
                  <GridItem>
                    <Menu placement="bottom-end">
                      <MenuButton
                        as={IconButton}
                        icon={<DotsThreeVertical />}
                        variant="ghost"
                        size="sm"
                        aria-label="Options"
                      />
                      <MenuList>
                        <MenuItem
                          icon={<MapTrifold size={16} />}
                          onClick={() => {
                            if (siteData.location?.coordinates) {
                              const { lat, lng } = siteData.location.coordinates;
                              window.open(`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`, '_blank');
                            }
                          }}
                        >
                          Open in Google Maps
                        </MenuItem>
                        <MenuItem
                          icon={<CopySimple size={16} />}
                          onClick={copyCoordinates}
                        >
                          Copy Coordinates
                        </MenuItem>
                        <MenuItem
                          icon={<PencilSimple size={16} />}
                          onClick={() => {
                            // Focus on the search input
                            const inputEl = document.getElementById('location');
                            if (inputEl) inputEl.focus();
                          }}
                        >
                          Edit Location
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  </GridItem>
                </Grid>
              </MotionBox>
            )}
          </GridItem>
          
          {/* Region and Country fields */}
          <GridItem colSpan={{ base: 12, md: 6 }}>
            <FormControl>
              <FormLabel htmlFor="region" fontWeight="medium">
                Region
              </FormLabel>
              <Input
                id="region"
                name="region"
                placeholder="e.g. Wiltshire, Brittany"
                value={siteData.location?.region || ""}
                onChange={(e) => {
                  setSiteData({
                    ...siteData,
                    location: {
                      ...siteData.location,
                      region: e.target.value
                    }
                  });
                }}
                variant="filled"
                bg={inputBg}
              />
              <FormHelperText>
                Province, state or region
              </FormHelperText>
            </FormControl>
          </GridItem>
          
          <GridItem colSpan={{ base: 12, md: 6 }}>
            <FormControl>
              <FormLabel htmlFor="country" fontWeight="medium">
                Country
              </FormLabel>
              <Input
                id="country"
                name="country"
                placeholder="e.g. United Kingdom, France"
                value={siteData.location?.country || ""}
                onChange={(e) => {
                  setSiteData({
                    ...siteData,
                    location: {
                      ...siteData.location,
                      country: e.target.value
                    }
                  });
                }}
                variant="filled"
                bg={inputBg}
              />
              <FormHelperText>
                Country where the site is located
              </FormHelperText>
            </FormControl>
          </GridItem>
        </Grid>
        
        {/* Location advice */}
        {(!siteData.location?.coordinates?.lat && !siteData.location?.coordinates?.lng) && (
          <Alert status="info" variant="subtle" borderRadius="md">
            <AlertIcon />
            <Box>
              <Text fontWeight="medium">No location selected yet</Text>
              <Text fontSize="sm">
                <b>For well-known sites:</b> Type name and select from dropdown suggestions<br/>
                <b>For obscure sites:</b> Type name and click the "Find Site" button (dropdown may not show these)<br/>
                <b>Alternative methods:</b> Click directly on map or use "Coords" button
              </Text>
            </Box>
          </Alert>
        )}
      </VStack>
      
      {/* Manual Coordinate Entry Modal */}
      <Modal isOpen={showManualEntryModal} onClose={() => setShowManualEntryModal(false)} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Manual Coordinate Entry</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <Text mb={4}>
              <b>Last resort method:</b> Use this when neither the dropdown suggestions nor the "Find Site" button locates your megalithic site.
            </Text>
            
            <Text mb={4} fontSize="sm" color={mutedColor}>
              <i>Need to find coordinates? You can use sites like <a href="https://www.latlong.net/" target="_blank" rel="noopener noreferrer" style={{textDecoration: 'underline'}}>latlong.net</a> or look up the site on Wikipedia which often lists exact coordinates.</i>
            </Text>
            <FormControl isInvalid={manualEntryError !== ''}>
              <FormLabel>Latitude</FormLabel>
              <Input
                placeholder="e.g. 51.178882"
                value={manualLat}
                onChange={(e) => setManualLat(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    document.getElementById('manual-lng-input').focus();
                  }
                }}
              />
              <FormHelperText>Valid range: -90 to 90</FormHelperText>
            </FormControl>

            <FormControl mt={4} isInvalid={manualEntryError !== ''}>
              <FormLabel>Longitude</FormLabel>
              <Input
                id="manual-lng-input"
                placeholder="e.g. -1.826215"
                value={manualLng}
                onChange={(e) => setManualLng(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    document.querySelector('button[data-set-coords-btn]')?.click();
                  }
                }}
              />
              <FormHelperText>Valid range: -180 to 180</FormHelperText>
              {manualEntryError && (
                <FormErrorMessage>{manualEntryError}</FormErrorMessage>
              )}
            </FormControl>
          </ModalBody>

          <ModalFooter>
            <Button
              colorScheme="yellow"
              mr={3}
              data-set-coords-btn
              onClick={() => {
                // Validate coordinates
                const lat = parseFloat(manualLat);
                const lng = parseFloat(manualLng);
                
                if (isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
                  setManualEntryError('Please enter valid coordinates (Latitude: -90 to 90, Longitude: -180 to 180)');
                  return;
                }
                
                // Reset error
                setManualEntryError('');
                
                // Use Google Maps Geocoder API to get address information for these coordinates
                const geocoder = new window.google.maps.Geocoder();
                geocoder.geocode({ location: { lat, lng } }, (results, status) => {
                  // Parse address and update site data similar to onMapClick
                  if (status === 'OK' && results[0]) {
                    const place = results[0];
                    
                    let addressData = {
                      address: place.formatted_address || `Location at ${lat.toFixed(6)}, ${lng.toFixed(6)}`,
                      street: '',
                      city: '',
                      region: '',
                      country: '',
                      postcode: '',
                      coordinates: {
                        lat: Number(lat),
                        lng: Number(lng)
                      }
                    };
                    
                    // Extract structured address components
                    if (place.address_components) {
                      for (const component of place.address_components) {
                        const types = component.types;
                        
                        if (types.includes('street_number') || types.includes('route')) {
                          addressData.street = addressData.street
                            ? `${addressData.street} ${component.long_name}`
                            : component.long_name;
                        }
                        if (types.includes('locality')) {
                          addressData.city = component.long_name;
                        }
                        if (types.includes('administrative_area_level_1')) {
                          addressData.region = component.long_name;
                        }
                        if (types.includes('country')) {
                          addressData.country = component.long_name;
                        }
                        if (types.includes('postal_code')) {
                          addressData.postcode = component.long_name;
                        }
                      }
                    }
                    
                    // Update site data
                    setSiteData({
                      ...siteData,
                      location: {
                        ...addressData,
                        coordinates: {
                          lat: Number(lat),
                          lng: Number(lng)
                        }
                      }
                    });
                    
                    // Keep site name in the autocomplete field
                    setAutocompleteValue(siteData.name || '');
                    
                    // Center map and update marker
                    if (map) {
                      map.panTo({ lat, lng });
                      setMapZoom(15);
                    }
                    setMarkerPosition({ lat, lng });
                    
                    // Close modal
                    setShowManualEntryModal(false);
                    
                    // Show success toast
                    toast({
                      title: "Location updated",
                      description: `Set coordinates: ${lat.toFixed(6)}, ${lng.toFixed(6)}`,
                      status: "success",
                      duration: 3000,
                      isClosable: true,
                    });
                  } else {
                    // Even if geocoding fails, still set the coordinates
                    const addressData = {
                      address: `Manual location at ${lat.toFixed(6)}, ${lng.toFixed(6)}`,
                      street: '',
                      city: '',
                      region: '',
                      country: '',
                      postcode: '',
                      coordinates: {
                        lat: Number(lat),
                        lng: Number(lng)
                      }
                    };
                    
                    setSiteData({
                      ...siteData,
                      location: addressData
                    });
                    
                    // Center map and update marker
                    if (map) {
                      map.panTo({ lat, lng });
                      setMapZoom(15);
                    }
                    setMarkerPosition({ lat, lng });
                    
                    // Close modal
                    setShowManualEntryModal(false);
                    
                    // Show success toast
                    toast({
                      title: "Location updated",
                      description: `Set coordinates: ${lat.toFixed(6)}, ${lng.toFixed(6)}`,
                      status: "success",
                      duration: 3000,
                      isClosable: true,
                    });
                  }
                });
              }}
            >
              Set Location
            </Button>
            <Button onClick={() => setShowManualEntryModal(false)}>Cancel</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};
